/**
 * OccupationalExam Types for MEV-Med Frontend
 * Type definitions for occupational exam catalog management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão do catálogo de exames ocupacionais
 */

/**
 * Enum for OccupationalExam status
 */
export enum OccupationalExamStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

/**
 * Classificação do exame (natureza). OCUPACIONAL = Avaliação Clínica Ocupacional
 * (código eSocial 0295); COMPLEMENTAR = demais. Distinto do filtro por ocasião
 * (OccupationalExamFilters.examType).
 */
export enum OccupationalExamType {
  OCUPACIONAL = 'OCUPACIONAL',
  COMPLEMENTAR = 'COMPLEMENTAR'
}

/**
 * OccupationalExam entity interface
 */
export interface OccupationalExam {
  id: number;
  examName: string;
  esocialCode?: string | null | undefined;
  tussCode?: string | null | undefined;
  defaultFrequency?: string | null | undefined;
  esocialDescription?: string | null | undefined;
  realizationValidity?: string | null | undefined;
  examType?: OccupationalExamType | null | undefined;
  realizationValidityLowRiskDays?: number | null | undefined;
  realizationValidityHighRiskDays?: number | null | undefined;
  tussDescription?: string | null | undefined;
  examProcedure?: string | null | undefined;
  deliveryTime?: string | null | undefined;
  examConduct?: string | null | undefined;
  criteria?: string | null | undefined;
  examPreparation?: string | null | undefined;
  bmiDescription?: string | null | undefined;

  // Exam type flags
  preEmployment: boolean;
  periodic: boolean;
  dismissal: boolean;
  complementary: boolean;
  returnToWork: boolean;
  riskChange: boolean;
  clinicalConsultation: boolean;

  // Work type flags
  foodIndustry: boolean;
  electrical: boolean;
  pta: boolean;
  confinedSpace: boolean;
  heightWork: boolean;
  forklift: boolean;
  selfPropelledMachine: boolean;
  hotWork: boolean;

  // Report flags
  eSocial: boolean;
  PPP: boolean;
  ASO: boolean;
  PPR: boolean;
  PCMSO: boolean;
  analyticalReport: boolean;
  PCA: boolean;

  // Metadata
  status: OccupationalExamStatus;

  // Computed arrays from backend
  examTypes?: string[];
  workTypes?: string[];
  reportTypes?: string[];

  // Alert counts (optional - returned when includeCounts=true)
  _counts?: {
    companies?: number;
    departments?: number;
    occupations?: number;
  };

  createdAt: string;
  updatedAt: string;
}

/**
 * Filters for listing occupational exams
 */
export interface OccupationalExamFilters {
  search?: string | undefined;
  examType?: 'preEmployment' | 'periodic' | 'dismissal' | 'complementary' | 'returnToWork' | 'riskChange' | 'clinicalConsultation' | undefined;
  workType?: 'foodIndustry' | 'electrical' | 'pta' | 'confinedSpace' | 'heightWork' | 'forklift' | 'selfPropelledMachine' | 'hotWork' | undefined;
  includeESocial?: boolean | undefined;
  includePPP?: boolean | undefined;
  includeASO?: boolean | undefined;
  status?: OccupationalExamStatus | undefined;
  page?: number | undefined;
  limit?: number | undefined;
  sortBy?: string | undefined;
  sortOrder?: 'ASC' | 'DESC' | undefined;
}

/**
 * Form data for creating/updating occupational exams
 * This represents the UI form state
 */
export interface OccupationalExamFormData {
  examName: string;
  esocialCode?: string | undefined;
  tussCode?: string | undefined;
  defaultFrequency?: string | undefined;
  esocialDescription?: string | undefined;
  realizationValidity?: string | undefined;
  examType?: OccupationalExamType | undefined;
  realizationValidityLowRiskDays?: number | undefined;
  realizationValidityHighRiskDays?: number | undefined;
  tussDescription?: string | undefined;
  examProcedure?: string | undefined;
  deliveryTime?: string | undefined;
  examConduct?: string | undefined;
  criteria?: string | undefined;
  examPreparation?: string | undefined;
  bmiDescription?: string | undefined;

  // Exam type flags
  preEmployment: boolean;
  periodic: boolean;
  dismissal: boolean;
  complementary: boolean;
  returnToWork: boolean;
  riskChange: boolean;
  clinicalConsultation: boolean;

  // Work type flags
  foodIndustry: boolean;
  electrical: boolean;
  pta: boolean;
  confinedSpace: boolean;
  heightWork: boolean;
  forklift: boolean;
  selfPropelledMachine: boolean;
  hotWork: boolean;

  // Report flags
  eSocial: boolean;
  PPP: boolean;
  ASO: boolean;
  PPR: boolean;
  PCMSO: boolean;
  analyticalReport: boolean;
  PCA: boolean;

  // Metadata
  status: OccupationalExamStatus;
}

/**
 * Response for list endpoint with pagination
 */
export interface OccupationalExamListResponse {
  exams: OccupationalExam[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Dependency check response
 */
export interface OccupationalExamDependencies {
  hasApplications: boolean;
  count: number;
  details?: {
    companies?: number;
    departments?: number;
    occupations?: number;
    employees?: number;
  };
}

/**
 * Props for UI components
 */
export interface OccupationalExamTableProps {
  exams: OccupationalExam[];
  onEdit?: (exam: OccupationalExam) => void;
  onDelete?: (id: number) => void;
  onView?: (exam: OccupationalExam) => void;
  loading?: boolean;
}

export interface OccupationalExamFormProps {
  initialData?: OccupationalExam;
  onSubmit: (data: OccupationalExamFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface OccupationalExamFiltersProps {
  filters: OccupationalExamFilters;
  onChange: (filters: OccupationalExamFilters) => void;
  onSearch: () => void;
}

/**
 * Exam type descriptions (Portuguese labels)
 */
export const ExamTypeLabels: Record<string, string> = {
  preEmployment: 'Admissional',
  periodic: 'Periódico',
  dismissal: 'Demissional',
  complementary: 'Complementar',
  returnToWork: 'Retorno ao Trabalho',
  riskChange: 'Mudança de Risco',
  clinicalConsultation: 'Consulta Clínica'
};

/**
 * Work type descriptions (Portuguese labels)
 */
export const WorkTypeLabels: Record<string, string> = {
  foodIndustry: 'Indústria Alimentícia',
  electrical: 'Eletricidade (NR-10)',
  pta: 'PTA (Programa de Proteção Auditiva)',
  confinedSpace: 'Espaço Confinado (NR-33)',
  heightWork: 'Trabalho em Altura (NR-35)',
  forklift: 'Empilhadeira',
  selfPropelledMachine: 'Máquina Autopropelida',
  hotWork: 'Trabalho a Quente'
};

/**
 * Report type descriptions (Portuguese labels)
 */
export const ReportTypeLabels: Record<string, string> = {
  eSocial: 'eSocial (S-2220)',
  PPP: 'PPP',
  ASO: 'ASO',
  PPR: 'PPR',
  PCMSO: 'PCMSO',
  analyticalReport: 'Relatório Analítico',
  PCA: 'PCA'
};
